import { Component, OnInit } from '@angular/core';
import { BizzCustomersService } from 'src/app/_business-dashboard/_services/bizz-customers.service';
import { SubscriptionPlanService } from 'src/app/_services/subscription-plan.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';

interface PlanFeature {
    key: string;
    label: string;
    included: boolean;
}

const PLAN_CONFIG_FEATURES_ENABLED = true;

// TODO re-enable: hide these specific feature rows from the Features grid pending later use.
// Overrides/save logic still runs on them as normal — only the display is filtered.
const HIDDEN_PLAN_FEATURE_LABELS = ['custom online store', 'standard customer app'];

@Component({
    selector: 'app-bizz-customer-plan-configuration',
    templateUrl: './bizz-customer-plan-configuration.component.html',
    styleUrls: ['./bizz-customer-plan-configuration.component.scss'],
})
export class BizzCustomerPlanConfigurationComponent implements OnInit {
    featuresEnabled = PLAN_CONFIG_FEATURES_ENABLED;

    plans: string[] = [];
    selectedPlan: string = '';
    isLoadingPlans: boolean = false;

    chargeTypeOptions = [
        { label: 'No Fee', value: 'none' },
        { label: 'Fixed ($)', value: 'fixed' },
        { label: 'Percentage (%)', value: 'percentage' },
    ];

    resetTypeOptions = [
        { label: 'Daily', value: 'Daily' },
        { label: 'Monthly', value: 'Monthly' },
        { label: 'Lifetime cap', value: 'FirstNTransactions' },
        { label: 'No free orders', value: 'None' },
    ];

    ordersUsedSoFar: number = 0;

    // Read-only — no admin-editable override exists for this yet. Falls back to the default
    // Stripe rate when the API doesn't send a value.
    stripePaymentGatewayFee: string = '2.9% + $0.30';

    // Per-section collapse state — all expanded by default.
    sectionExpanded: { [key: string]: boolean } = {
        storeOffer: true,
        accountStatus: true,
        freeTransactions: true,
        platformFee: true,
        paymentGateway: true,
        limits: true,
        features: true,
    };

    toggleSection(key: string): void {
        this.sectionExpanded[key] = !this.sectionExpanded[key];
    }

    // ----- "Online Store for Free" admin offer -----
    storeOffer = {
        availSaveOffer: false,
        fileUrl: '',
        status: 0,
        completedDate: null as string | null,
        showNotificationBar: true,
    };
    isSavingStoreOffer: boolean = false;

    overrides: any = {};
    isSavingOverrides: boolean = false;

    // Decoupled from `effective.feeAmount` so re-binding `effective` on every keystroke (via
    // recompute()) doesn't reformat the input mid-type (PrimeNG inputNumber decimal-entry bug —
    // typing "0.50" would get mangled into "50.00"). Synced from `effective` in recompute(),
    // committed back to overrides only on blur.
    feeAmountDraft: number = 0;

    // Cached computed state (recomputed only when plan/overrides change — NOT per change-detection cycle).
    effective: any = {};
    planFeatures: PlanFeature[] = [];
    overrideCount: number = 0;
    isFormValid: boolean = true;

    private subscriptionPlans: any[] = [];

    // transactionConfig.IsTransactionable keyed by plan title (lowercase) — populated from
    // getSubscriptionPlanList() (V1), the only endpoint that returns transactionConfig.
    private transactionableByPlan: Map<string, boolean> = new Map();

    // Feature-toggle flags as returned by the businessplan API, keyed by the keyword used to match
    // against a plan package's title (same matching scheme as `featureIncluded()` on save).
    private apiFeatureFlags: { [labelKeyword: string]: boolean } = {};

    constructor(
        public _bizzCustomerService: BizzCustomersService,
        private _subscriptionPlanService: SubscriptionPlanService,
        private _connMessageService: ConnMessageService,
        private _businessSetupService: BusinessSetupService
    ) {}

    ngOnInit(): void {
        const activeItem = this._bizzCustomerService.activeItem || {};
        const currentSubscription = activeItem.currentSubscription || {};
        const currentPlanName = String(currentSubscription.planName || currentSubscription.title || '').toLowerCase();

        this.loadStoreOffer();
        this.seedOverridesFromApi(activeItem);
        this.stripePaymentGatewayFee =
            this.readField(activeItem, 'stripePaymentGatewayFee', 'StripePaymentGatewayFee') ||
            this.stripePaymentGatewayFee;

        // Load V1 plans (for transactionConfig.IsTransactionable) in parallel with V2.
        this._subscriptionPlanService.getSubscriptionPlanList().subscribe({
            next: (res: any) => {
                const plans = res?.data ?? res?.Data ?? res ?? [];
                (Array.isArray(plans) ? plans : []).forEach((p: any) => {
                    const title = String(p?.title ?? p?.Title ?? '').trim().toLowerCase();
                    if (!title) return;
                    const tc = p?.transactionConfig ?? p?.TransactionConfig;
                    const isT = tc?.IsTransactionable === true || tc?.isTransactionable === true;
                    this.transactionableByPlan.set(title, isT);
                });
            },
            error: () => {},
        });

        this.isLoadingPlans = true;
        this._subscriptionPlanService.getSubscriptionPlanListV2().subscribe({
            next: (res: any) => {
                this.isLoadingPlans = false;
                if (!res?.success) {
                    return;
                }

                this.subscriptionPlans = (res.data || []).filter(
                    (p: any) => String(p?.productType || '').toLowerCase() === 'subscription'
                );

                this.plans = this.subscriptionPlans.map((p: any) => p.title);
                this.selectedPlan =
                    this.subscriptionPlans.find((p: any) => String(p.title || '').toLowerCase() === currentPlanName)
                        ?.title || this.plans[0] || '';
                this.seedFeatureOverridesFromApi();
                this.recompute();
            },
            error: () => {
                this.isLoadingPlans = false;
            },
        });
    }

    private chargeTypeFromApi(apiType: string): string {
        const normalized = String(apiType || '').toLowerCase();
        if (normalized === 'fixed') return 'fixed';
        if (normalized === 'percentage') return 'percentage';
        return 'none';
    }

    /** Seed numeric/charge-type overrides directly from the saved businessplan API fields. */
    private seedOverridesFromApi(activeItem: any): void {
        const overrides: any = {};
        const setIfPresent = (overrideKey: string, value: any, transform: (v: any) => any = (v) => v) => {
            if (value !== undefined && value !== null) {
                overrides[overrideKey] = transform(value);
            }
        };

        setIfPresent('freeOrdersAllowed', activeItem.freeOrders, Number);
        const resetTypeRaw = activeItem.freeOrdersResetType
            ?? activeItem.transactionConfig?.resetType
            ?? activeItem.transactionConfig?.ResetType;
        setIfPresent('resetType', resetTypeRaw, String);
        setIfPresent('chargeType', activeItem.platformFeeType, (v) => this.chargeTypeFromApi(v));
        setIfPresent('feeAmount', activeItem.platformFeeAmount, Number);
        setIfPresent('allowedProducts', activeItem.noOfProducts, Number);
        setIfPresent('storeLocations', activeItem.noOfLocations, Number);
        setIfPresent('inventoryLocations', activeItem.noOfInventoryLocations, Number);
        setIfPresent('registers', activeItem.noOfRegisters, Number);
        setIfPresent('staffAccounts', activeItem.noOfUsers, Number);

        this.overrides = overrides;
        this.ordersUsedSoFar = Number(activeItem.freeOrdersUsed) || 0;

        // Field casing on this API is inconsistent per field (PascalCase vs camelCase) — check both
        // spellings rather than assuming one convention holds across the whole payload.
        this.apiFeatureFlags = {
            'standard online store': this.readField(activeItem, 'standardOnlineStore', 'StandardOnlineStore') === true,
            'custom online store': this.readField(activeItem, 'customOnlineStore', 'CustomOnlineStore') === true,
            'pos lite': this.readField(activeItem, 'hasPOSLite', 'HasPOSLite') === true,
            'pos pro': this.readField(activeItem, 'hasPOS', 'HasPOS') === true,
            'standard loyalty': this.readField(activeItem, 'enableLoyaltyReward', 'EnableLoyaltyReward') === true,
            'advanced loyalty': this.readField(activeItem, 'advancedLoyaltyProgram', 'AdvancedLoyaltyProgram') === true,
            'customer app': this.readField(activeItem, 'standardCustomerApp', 'StandardCustomerApp') === true,
        };
    }

    /** Map `apiFeatureFlags` onto the now-known plan feature rows (label keyword match) as overrides. */
    private seedFeatureOverridesFromApi(): void {
        const packages = this.getFeaturePackages(this.selectedPlan);
        const featureOverrides: any = {};
        packages.forEach((pkg: any, index: number) => {
            const key = index + '__' + String(pkg.title ?? '');
            const label = String(pkg.title || '').toLowerCase();
            Object.keys(this.apiFeatureFlags).forEach((keyword) => {
                if (label.includes(keyword)) {
                    featureOverrides[key] = this.apiFeatureFlags[keyword];
                }
            });
        });

        if (Object.keys(featureOverrides).length) {
            this.overrides = { ...this.overrides, features: featureOverrides };
        }
    }

    // ---- "Online Store for Free" offer (admin) ----
    private businessRecordSource(): any {
        const item = this._bizzCustomerService.activeItem || {};
        // Fields can live on the root payload or under businessSetup (object or array[0]).
        const bs = Array.isArray(item.businessSetup) ? item.businessSetup[0] : item.businessSetup;
        return { ...(bs || {}), ...item };
    }

    private readField(src: any, ...keys: string[]): any {
        for (const k of keys) {
            if (src?.[k] !== undefined && src?.[k] !== null) {
                return src[k];
            }
        }
        return null;
    }

    private loadStoreOffer(): void {
        const src = this.businessRecordSource();
        this.storeOffer = {
            availSaveOffer: this.readField(src, 'availSaveOffer', 'AvailSaveOffer') === true,
            fileUrl: String(this.readField(src, 'storeContentFileUrl', 'StoreContentFileUrl') || '').trim(),
            status: Number(this.readField(src, 'storeContentStatus', 'StoreContentStatus') || 0),
            completedDate: this.readField(src, 'storeContentCompletedDate', 'StoreContentCompletedDate'),
            showNotificationBar:
                this.readField(src, 'storeContentShowNotificationBar', 'StoreContentShowNotificationBar') !== false,
        };
    }

    // ---- Account Status (Stripe connection + merchant self-reported flags) ----

    /**
     * extractCustomerDetailPayload() (bizz-customer-detail.component.ts) picks whichever node in the
     * adminaccountdetail response scores highest on customer-identity fields (firstName/email/etc.) as
     * `activeItem` — Stripe/plan fields aren't guaranteed to live on that same node (they can be one
     * level deeper, e.g. under a sibling businessSetup/subscription object). businessRecordSource()'s
     * one-level merge covers the common case; this does a full breadth-first search of the whole
     * activeItem tree so a field found deeper (or renamed) still resolves instead of silently reading
     * as "not set".
     */
    private deepReadField(...keys: string[]): any {
        const targets = keys.map((k) => k.toLowerCase());
        const visited = new Set<any>();
        const queue: any[] = [this._bizzCustomerService.activeItem || {}];

        while (queue.length) {
            const node = queue.shift();
            if (!node || typeof node !== 'object' || visited.has(node)) {
                continue;
            }
            visited.add(node);

            if (Array.isArray(node)) {
                queue.push(...node);
                continue;
            }

            for (const [key, value] of Object.entries(node)) {
                if (targets.includes(key.toLowerCase()) && value !== undefined && value !== null) {
                    return value;
                }
            }
            for (const value of Object.values(node)) {
                if (value && typeof value === 'object') {
                    queue.push(value);
                }
            }
        }

        return null;
    }

    /** Non-empty StripeConnectedAccountID is the only signal used — there's no separate boolean field for this. */
    get stripeConnectedAccountId(): string {
        return String(
            this.deepReadField('stripeConnectedAccountID', 'StripeConnectedAccountID', 'stripeConnectedAccountId') || ''
        ).trim();
    }

    get isStripeConnected(): boolean {
        return this.stripeConnectedAccountId.length > 0;
    }

    get stripeOnboardingUrl(): string {
        return String(this.deepReadField('stripeOnboardingUrl', 'StripeOnboardingUrl') || '').trim();
    }

    get cancellationRequested(): boolean {
        return this.deepReadField('cancellationRequest', 'CancellationRequest') === true;
    }

    get upgradeRequested(): boolean {
        return this.deepReadField('upgradeRequested', 'UpgradeRequested') === true;
    }

    /** Hidden: Physical store / Mobile app rows in Account Status. Flip to true to re-enable. */
    ACCOUNT_STATUS_ONBOARDING_FLAGS_ENABLED = false;

    /** Merchant's own onboarding "where do you sell" answers — not plan entitlements. Display-only here. */
    get hasPhysicalStoreFlag(): boolean {
        return this.deepReadField('hasPhysicalStore', 'HasPhysicalStore') === true;
    }

    get hasMobileAppFlag(): boolean {
        return this.deepReadField('hasMobileApp', 'HasMobileApp') === true;
    }

    /**
     * Hidden: this "Free Online Store Setup" card duplicates the "Store Content" card
     * in bizz-customer-personal-info. Flip to true to re-enable.
     */
    STORE_OFFER_CARD_ENABLED = false;

    /** Hidden: Plan Configuration hero banner. Flip to true to re-enable. */
    PC_HERO_ENABLED = false;

    /** Section is only relevant when the merchant opted into the free store-setup offer. */
    get showStoreOffer(): boolean {
        return this.storeOffer.availSaveOffer === true;
    }

    /** Merchant has submitted their store details (a file was uploaded). */
    get storeOfferHasFile(): boolean {
        return this.storeOffer.fileUrl.length > 0;
    }

    /** Status 2 = completed. */
    get storeOfferCompleted(): boolean {
        return this.storeOffer.status === 2;
    }

    /** Admin can only mark complete once the merchant has uploaded their details and it isn't already done. */
    get canCompleteStoreOffer(): boolean {
        return this.storeOfferHasFile && !this.storeOfferCompleted;
    }

    completeStoreOffer(): void {
        if (!this.canCompleteStoreOffer || this.isSavingStoreOffer) {
            return;
        }

        const item = this._bizzCustomerService.activeItem || {};
        // Same resolution path as the Customer Info tab's Store Content card — the nested
        // currentSubscription field is the reliable one; top-level fields can hold an unrelated value.
        const businessName = String(item?.currentSubscription?.businessNameWithDomain ?? '');

        this.isSavingStoreOffer = true;
        this._businessSetupService
            .updateStoreContentStatus(
                { Status: 2, ShowNotificationBar: false, BusinessNameWithDomain: businessName },
                businessName
            )
            .subscribe({
                next: (res: any) => {
                    this.isSavingStoreOffer = false;
                    const success = res?.success ?? res?.Success;
                    if (success === false) {
                        this._connMessageService.showToastMessage('Store setup offer could not be marked complete.', 'error');
                        return;
                    }

                    const data = res?.data ?? res?.Data ?? {};
                    const completedDate =
                        data?.storeContentCompletedDate ?? data?.StoreContentCompletedDate ?? new Date().toISOString();

                    // Mark complete: status -> 2, stamp completion date, stop the merchant's notification bar.
                    this.storeOffer.status = 2;
                    this.storeOffer.completedDate = completedDate;
                    this.storeOffer.showNotificationBar = false;

                    // Mirror onto the shared activeItem so the rest of the page (e.g. the Customer Info tab) reflects the change.
                    const targets = [item, Array.isArray(item.businessSetup) ? item.businessSetup[0] : item.businessSetup];
                    targets.forEach((t: any) => {
                        if (!t) return;
                        t.storeContentStatus = 2; t.StoreContentStatus = 2;
                        t.storeContentCompletedDate = completedDate; t.StoreContentCompletedDate = completedDate;
                        t.storeContentShowNotificationBar = false; t.StoreContentShowNotificationBar = false;
                    });

                    this._connMessageService.showToastMessage(
                        'Store setup offer marked as completed for this merchant.',
                        'success'
                    );
                },
                error: () => {
                    this.isSavingStoreOffer = false;
                    this._connMessageService.showToastMessage('Store setup offer could not be marked complete.', 'error');
                },
            });
    }

    private getPlanData(planName: string): any {
        return this.subscriptionPlans.find((p) => p.title === planName) || {};
    }

    /** True when the selected plan's transactionConfig.IsTransactionable is true (sourced from V1 API). */
    get isTransactionable(): boolean {
        return this.transactionableByPlan.get(this.selectedPlan.trim().toLowerCase()) === true;
    }

    private parseLimitFromPackages(planName: string, keyword: string): number | null {
        const planData = this.getPlanData(planName);
        const packages = planData.planPackages || planData.PlanPackages || [];
        const pkg = packages.find((p: any) =>
            String(p.title || '').toLowerCase().includes(keyword)
        );
        if (!pkg) {
            return null;
        }
        if (/unlimited/i.test(pkg.title)) {
            return null;
        }
        const match = String(pkg.title).match(/\d+/);
        return match ? Number(match[0]) : null;
    }

    // Feature rows to hide — either duplicated in "Limits & Access" (counts) or not relevant here.
    private excludedFeatureKeywords: string[] = [
        'product',
        'inventory location',
        'staff account',
        'register',
        'user',
        'social media selling',
        'live support',
        'support',
    ];

    private getFeaturePackages(planName: string): any[] {
        const planData = this.getPlanData(planName);
        const packages = planData.planPackages || planData.PlanPackages || [];
        return packages.filter((pkg: any) => {
            const title = String(pkg?.title || '').toLowerCase();
            return !this.excludedFeatureKeywords.some((kw) => title.includes(kw));
        });
    }

    private getTypedPackagesArray(planName: string): any[] {
        const planData = this.getPlanData(planName);
        const candidateKeys = ['featureData', 'FeatureData', 'planPackages', 'PlanPackages', 'packages', 'Packages', 'limits', 'Limits', 'planLimits', 'PlanLimits'];
        for (const key of candidateKeys) {
            if (Array.isArray(planData[key]) && planData[key].some((p: any) => p?.typeString)) {
                return planData[key];
            }
        }
        return [];
    }

    private parseLimitByTypeString(planName: string, typeStrings: string[]): number | null {
        const packages = this.getTypedPackagesArray(planName);
        const pkg = packages.find((p: any) =>
            typeStrings.some((t) => String(p.typeString || '').toLowerCase() === t.toLowerCase())
        );
        if (!pkg || pkg.unlimited === true) {
            return null;
        }
        const num = Number(pkg.value);
        return Number.isFinite(num) ? num : null;
    }

    private buildPlanDefaults() {
        const planName = this.selectedPlan;
        const isFree = planName === 'Free';

        return {
            freeOrdersAllowed: isFree ? 5 : 0,
            resetType: 'Monthly',
            feeEnabled: isFree,
            chargeType: isFree ? 'fixed' : 'none',
            feeAmount: isFree ? 0.99 : 0,
            allowedProducts: this.parseLimitFromPackages(planName, 'product'),
            // "Locations" (typeString) is the merchant's Store Locations count — distinct from the
            // "Inventory Locations" package limit (e.g. Free=1 store / 1 inventory location vs
            // Premium=2 stores / 10 inventory locations). Do not collapse these into one field.
            storeLocations: this.parseLimitByTypeString(planName, ['Locations']),
            inventoryLocations: this.parseLimitFromPackages(planName, 'inventory location'),
            registers: this.parseLimitByTypeString(planName, ['Registers']),
            staffAccounts:
                this.parseLimitByTypeString(planName, ['Users', 'Staff']) ??
                this.parseLimitFromPackages(planName, 'staff account'),
        };
    }

    // `value ?? default` is wrong here: clearing a number field emits `null`, which `??` treats as
    // "no override" and silently falls back to the default — masking the invalid/empty state.
    // Use explicit presence checks instead, so an intentional null override is preserved.
    private resolve(key: string, defaultValue: any): any {
        return this.isOverridden(key) ? this.overrides[key] : defaultValue;
    }

    /** Recompute cached state. Call only on data load or override changes — never bind this to the template. */
    private recompute(): void {
        const defaults = this.buildPlanDefaults();
        const chargeType = this.resolve('chargeType', defaults.chargeType);
        const resetType = this.resolve('resetType', defaults.resetType);

        this.effective = {
            // "None" means no free-order policy at all — force 0 so it's never confused with
            // "Lifetime cap" (FirstNTransactions) sharing the same allowed count.
            freeOrdersAllowed: resetType === 'None' ? 0 : this.resolve('freeOrdersAllowed', defaults.freeOrdersAllowed),
            resetType,
            feeEnabled: this.resolve('feeEnabled', defaults.feeEnabled),
            chargeType,
            feeAmount: chargeType === 'none' ? 0 : this.resolve('feeAmount', defaults.feeAmount),
            allowedProducts: this.resolve('allowedProducts', defaults.allowedProducts),
            storeLocations: this.resolve('storeLocations', defaults.storeLocations),
            inventoryLocations: this.resolve('inventoryLocations', defaults.inventoryLocations),
            registers: this.resolve('registers', defaults.registers),
            staffAccounts: this.resolve('staffAccounts', defaults.staffAccounts),
        };
        this.feeAmountDraft = this.effective.feeAmount;

        const packages = this.getFeaturePackages(this.selectedPlan);
        const featureOverrides = this.overrides.features || {};
        this.planFeatures = packages.map((pkg: any, index: number) => {
            // Unique per row — never use pkg.id alone (API returns colliding ids, which would link toggles).
            const key = index + '__' + String(pkg.title ?? '');
            const planIncluded = pkg.included === true || pkg.included === 'true';
            const included = Object.prototype.hasOwnProperty.call(featureOverrides, key)
                ? featureOverrides[key]
                : planIncluded;
            return { key, label: pkg.title, included };
        });

        let count = 0;
        if (this.isOverridden('freeOrdersAllowed') || this.isOverridden('resetType')) count++;
        if (this.isOverridden('feeEnabled') || this.isOverridden('chargeType') || this.isOverridden('feeAmount')) count++;
        if (this.isOverridden('allowedProducts') || this.isOverridden('storeLocations') || this.isOverridden('inventoryLocations') || this.isOverridden('registers') || this.isOverridden('staffAccounts')) count++;
        if (this.overrides.features && Object.keys(this.overrides.features).length) count++;
        this.overrideCount = count;

        const feeAmountValid = this.effective.chargeType === 'none' || this.effective.feeAmount > 0;
        this.isFormValid = this.effective.freeOrdersAllowed !== null && feeAmountValid;
    }

    /** Daily/Monthly self-correct at the next reset, so a stale overage there is harmless. Lifetime
     * cap (FirstNTransactions) and None never reset usage back to 0 — lowering the allowance below
     * what's already used permanently locks the merchant out of free orders unless the admin
     * notices and raises it again, so flag it instead of staying silent. */
    get freeOrdersOverageWarning(): boolean {
        return (
            (this.effective.resetType === 'FirstNTransactions' || this.effective.resetType === 'None') &&
            this.effective.freeOrdersAllowed !== null &&
            this.ordersUsedSoFar > this.effective.freeOrdersAllowed
        );
    }

    /** No `lastResetDate`/`nextResetDate` field exists anywhere in the customer-detail API payload,
     * so this can't reflect what the backend job actually did last — it's a deterministic estimate
     * from the reset type's calendar rule (next midnight/1st-of-month UTC), labeled as such so it
     * isn't mistaken for an authoritative timestamp. Surfaced for support tickets like "why did my
     * fee suddenly start charging." */
    get nextFreeOrdersResetEstimate(): string | null {
        const now = new Date();
        if (this.effective.resetType === 'Daily') {
            const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
            return next.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' (midnight UTC)';
        }
        if (this.effective.resetType === 'Monthly') {
            const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
            return next.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' (midnight UTC)';
        }
        return null;
    }

    get freeOrdersUsagePercent(): number {
        if (!this.effective.freeOrdersAllowed) {
            return 0;
        }
        return Math.min(100, (this.ordersUsedSoFar / this.effective.freeOrdersAllowed) * 100);
    }

    get limitsSummary(): string {
        const fmt = (value: number | null, label: string) =>
            value === null ? `Unlimited ${label}` : `${value} ${label}`;
        return [
            fmt(this.effective.allowedProducts, 'products'),
            fmt(this.effective.storeLocations, 'stores'),
            fmt(this.effective.inventoryLocations, 'inventory'),
            fmt(this.effective.registers, 'registers'),
            fmt(this.effective.staffAccounts, 'staff'),
        ].join(' · ');
    }

    get enabledFeatureCount(): number {
        return this.visiblePlanFeatures.filter((f) => f.included).length;
    }

    /** `planFeatures` minus the rows staged as hidden via HIDDEN_PLAN_FEATURE_LABELS — the
     * template iterates this instead of `planFeatures` directly so hidden rows never render,
     * while overrides/save logic (keyed off the full `planFeatures` list) is unaffected. */
    get visiblePlanFeatures(): PlanFeature[] {
        return this.planFeatures.filter(
            (f) => !HIDDEN_PLAN_FEATURE_LABELS.includes(String(f.label || '').trim().toLowerCase())
        );
    }

    isOverridden(key: string): boolean {
        return Object.prototype.hasOwnProperty.call(this.overrides, key);
    }

    isFeatureOverridden(key: string): boolean {
        return !!this.overrides.features && Object.prototype.hasOwnProperty.call(this.overrides.features, key);
    }

    // `planFeatures` is rebuilt as a new array on every override change. Without trackBy, *ngFor
    // diffs by object identity and would destroy/recreate every row (and its p-inputSwitch) on
    // a single toggle — visually looking like sibling switches flip too. Track by the stable key instead.
    trackByFeatureKey(_index: number, feature: PlanFeature): string {
        return feature.key;
    }

    setOverride(key: string, value: any): void {
        this.overrides = { ...this.overrides, [key]: value };
        this.recompute();
    }

    commitFeeAmount(): void {
        this.setOverride('feeAmount', this.feeAmountDraft);
    }

    setFeatureOverride(key: string, value: any): void {
        this.overrides = {
            ...this.overrides,
            features: { ...(this.overrides.features || {}), [key]: value },
        };
        this.recompute();
    }

    resetToPlanDefaults(): void {
        this.overrides = {};
        this.recompute();
        this._connMessageService.showToastMessage('Plan configuration reset to plan defaults.', 'success');
    }

    /** Looks up a planFeatures row's `included` flag by matching its label, case-insensitively. */
    private featureIncluded(...labelKeywords: string[]): boolean {
        const feature = this.planFeatures.find((f) =>
            labelKeywords.some((kw) => String(f.label || '').toLowerCase().includes(kw))
        );
        return feature ? feature.included : false;
    }

    private feeTypeToApi(chargeType: string): string {
        if (chargeType === 'fixed') return 'Fixed';
        if (chargeType === 'percentage') return 'Percentage';
        return 'None';
    }

    saveOverrides(): void {
        if (this.isSavingOverrides) {
            return;
        }

        const activeItem = this._bizzCustomerService.activeItem || {};
        const request = {
            BusinessId: Number(activeItem.businessId),
            UserID: activeItem.userId,

            FreeOrders: this.effective.freeOrdersAllowed,
            FreeOrdersUsed: this.ordersUsedSoFar,
            FreeOrdersResetType: this.effective.resetType,

            PlatformFeeType: this.feeTypeToApi(this.effective.chargeType),
            PlatformFeeAmount: this.effective.feeAmount,

            NoOfProducts: this.effective.allowedProducts,
            NoOfLocations: this.effective.storeLocations,
            NoOfInventoryLocations: this.effective.inventoryLocations,
            NoOfRegisters: this.effective.registers,
            NoOfUsers: this.effective.staffAccounts,

            StandardOnlineStore: this.featureIncluded('standard online store'),
            CustomOnlineStore: this.featureIncluded('custom online store'),
            POSLite: this.featureIncluded('pos lite'),
            POSPro: this.featureIncluded('pos pro'),
            EnableLoyaltyReward: this.featureIncluded('standard loyalty'),
            AdvancedLoyaltyProgram: this.featureIncluded('advanced loyalty'),
            StandardCustomerApp: this.featureIncluded('customer app'),
        };

        this.isSavingOverrides = true;
        this._bizzCustomerService.editBusinessPlan(request).subscribe({
            next: (res: any) => {
                this.isSavingOverrides = false;
                if (res?.success === false) {
                    this._connMessageService.showToastMessage(
                        res?.message || 'Failed to save business plan.',
                        'error'
                    );
                    return;
                }
                this._connMessageService.showToastMessage(
                    'Business plan updated successfully.',
                    'success'
                );
            },
            error: () => {
                this.isSavingOverrides = false;
                this._connMessageService.showToastMessage(
                    'Failed to save business plan. Please try again.',
                    'error'
                );
            },
        });
    }
}
